import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer } from "lucide-react";

export default function InvoicePrint() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const autoPrint = params.get("auto") === "print";
    const [inv, setInv] = useState(null);
    const [tpl, setTpl] = useState(null);

    useEffect(() => {
        api.get(`/invoices/${id}`).then(async r => {
            setInv(r.data);
            if (r.data.template_id) {
                try { const t = await api.get(`/invoice-templates`); setTpl(t.data.find(x => x.id === r.data.template_id) || t.data[0]); }
                catch {}
            } else {
                const t = await api.get(`/invoice-templates`); setTpl(t.data.find(x => x.is_default) || t.data[0]);
            }
        }).catch(() => setInv(false));
    }, [id]);

    useEffect(() => {
        if (autoPrint && inv && tpl) {
            const t = setTimeout(() => window.print(), 600);
            return () => clearTimeout(t);
        }
    }, [autoPrint, inv, tpl]);

    if (inv === false) return <div className="p-12 text-center">Invoice not found</div>;
    if (!inv) return <div className="p-12 text-center text-stone-500">Loading…</div>;

    const accent = tpl?.accent_color || "#065f46";
    const style = tpl?.style || "classic";
    const cols = tpl?.columns || ["serial", "name", "hsn", "quantity", "rate", "amount"];

    return (
        <div className="bg-stone-100 min-h-screen py-8 print:bg-white print:py-0" data-testid="invoice-print">
            <div className="max-w-3xl mx-auto px-4 print:px-0 print:max-w-none">
                <div className="flex justify-end gap-2 mb-4 print:hidden">
                    <Button onClick={() => window.print()} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="print-btn"><Printer className="w-4 h-4 mr-2" />Print / Save PDF</Button>
                </div>

                <div className="bg-white shadow-sm border border-stone-200 print:border-0 print:shadow-none" style={{ borderTop: `4px solid ${accent}` }}>
                    <div className={`p-8 ${style === "tally" ? "border-b-2 border-stone-900" : "border-b border-stone-200"}`}>
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-4">
                                {tpl?.logo_url && <img src={tpl.logo_url} alt="logo" className="w-16 h-16 object-contain" />}
                                <div>
                                    <h1 className="font-serif text-2xl uppercase" style={{ color: accent }}>{tpl?.header_text || "Hahnemann Pharmacy & Laboratory"}</h1>
                                    {tpl?.address_text && <p className="text-xs text-stone-600 mt-1 whitespace-pre-line">{tpl.address_text}</p>}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">{style === "tally" ? "TAX INVOICE" : "Invoice"}</div>
                                <div className="font-mono text-lg">{inv.invoice_number}</div>
                                <div className="text-xs text-stone-500 mt-1">Date: {(inv.created_at || "").slice(0, 10)}</div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 p-8 gap-6 border-b border-stone-200">
                        <div>
                            <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-2">Bill to</div>
                            <div className="font-medium">{inv.customer.name}</div>
                            <div className="text-xs text-stone-600 whitespace-pre-line">
                                {inv.customer.address_line1}{inv.customer.address_line2 ? `\n${inv.customer.address_line2}` : ""}
                                {(inv.customer.city || inv.customer.state) ? `\n${[inv.customer.city, inv.customer.state, inv.customer.pincode].filter(Boolean).join(", ")}` : ""}
                            </div>
                            {inv.customer.phone && <div className="text-xs text-stone-600 mt-1">Ph: {inv.customer.phone}</div>}
                            {inv.customer.gst_number && <div className="text-xs text-stone-600">GSTIN: {inv.customer.gst_number}</div>}
                        </div>
                        <div className="text-right text-xs text-stone-600">
                            {inv.customer.customer_type && <div>Customer type: <span className="uppercase tracking-widest">{inv.customer.customer_type}</span></div>}
                            {inv.created_by_name && <div>Prepared by: {inv.created_by_name}</div>}
                            {inv.status === "paid" && <div className="inline-block mt-2 px-3 py-1 text-sky-700 border border-sky-700 uppercase tracking-widest">Paid</div>}
                        </div>
                    </div>

                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-stone-50 border-b-2 border-stone-300">
                                {cols.includes("serial") && <th className="text-left p-3 text-[10px] uppercase tracking-widest text-stone-600">#</th>}
                                {cols.includes("name") && <th className="text-left p-3 text-[10px] uppercase tracking-widest text-stone-600">Description</th>}
                                {cols.includes("hsn") && <th className="text-left p-3 text-[10px] uppercase tracking-widest text-stone-600">HSN</th>}
                                {cols.includes("quantity") && <th className="text-right p-3 text-[10px] uppercase tracking-widest text-stone-600">Qty</th>}
                                {cols.includes("rate") && <th className="text-right p-3 text-[10px] uppercase tracking-widest text-stone-600">Rate</th>}
                                {cols.includes("amount") && <th className="text-right p-3 text-[10px] uppercase tracking-widest text-stone-600">Amount</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {inv.items.map((it, i) => (
                                <tr key={i} className="border-b border-stone-200">
                                    {cols.includes("serial") && <td className="p-3 text-stone-500">{i + 1}</td>}
                                    {cols.includes("name") && <td className="p-3"><div>{it.name}</div>{it.size_label && <div className="text-xs text-stone-500">{it.size_label}</div>}</td>}
                                    {cols.includes("hsn") && <td className="p-3 text-stone-500 text-xs">{it.hsn}</td>}
                                    {cols.includes("quantity") && <td className="p-3 text-right">{it.quantity}</td>}
                                    {cols.includes("rate") && <td className="p-3 text-right">{formatINR(it.rate)}</td>}
                                    {cols.includes("amount") && <td className="p-3 text-right font-medium">{formatINR(it.amount)}</td>}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="grid grid-cols-2 p-8 gap-6">
                        <div className="text-xs text-stone-600 whitespace-pre-line">
                            {inv.terms && <><div className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-2">Terms</div>{inv.terms}</>}
                        </div>
                        <div className="text-sm space-y-1">
                            <Row label="Subtotal" v={inv.subtotal} />
                            {inv.discount_amount > 0 && <Row label={`Discount`} v={-inv.discount_amount} muted />}
                            <Row label="After discount" v={inv.after_discount} muted />
                            {inv.cgst_pct != null && inv.sgst_pct != null ? (
                                <>
                                    <Row label={`CGST @ ${inv.cgst_pct}%`} v={+(inv.gst_amount / 2).toFixed(2)} muted />
                                    <Row label={`SGST @ ${inv.sgst_pct}%`} v={+(inv.gst_amount / 2).toFixed(2)} muted />
                                </>
                            ) : (
                                <Row label={`IGST @ ${inv.igst_pct ?? inv.gst_pct}%`} v={inv.gst_amount} muted />
                            )}
                            {inv.special_discount > 0 && <Row label="Special discount" v={-inv.special_discount} muted />}
                            {inv.cash_discount > 0 && <Row label="Cash discount" v={-inv.cash_discount} muted />}
                            {Math.abs(inv.round_off) > 0 && <Row label="Round off" v={inv.round_off} muted />}
                            <div className="flex justify-between border-t-2 border-stone-300 pt-2 mt-2 font-serif text-xl"><span>Total</span><span>{formatINR(inv.total)}</span></div>
                        </div>
                    </div>

                    {(tpl?.show_signature || tpl?.footer_text) && (
                        <div className="p-8 border-t border-stone-200 grid grid-cols-2 items-end gap-6">
                            <div className="text-xs text-stone-500">{tpl?.footer_text}</div>
                            {tpl?.show_signature && (
                                <div className="text-right">
                                    <div className="border-t border-stone-300 mt-12 pt-2 inline-block px-8">
                                        <div className="text-xs text-stone-500">Authorised Signatory</div>
                                        <div className="text-xs">for {tpl?.header_text || "Hahnemann Pharmacy & Laboratory"}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <style>{`@media print { @page { size: A4; margin: 12mm } body { background: white } }`}</style>
        </div>
    );
}

const Row = ({ label, v, muted }) => (
    <div className={`flex justify-between ${muted ? "text-stone-500" : ""}`}>
        <span>{label}</span><span>{v < 0 ? "− " : ""}{formatINR(Math.abs(v))}</span>
    </div>
);
